<template lang="pug">
  div
    doc-markdown(source=`
    ## Profiling Question \`profiling-question\`
    Wrapper enclosing the different profiling inputs (\`profiling-input\`, \`profiling-select\`, \`profiling-checkbox\`, etc) that we use in our profiling forms. It achieves different goals:
    - It Streamlines and encloses the styling and presentation (by wrapping the question label, the optional description guidance and the optional escort message around the inputs)
    - Encapsulates the swiping logic on mobile (so that each \`profiling-question\` corresponds to one swipable slide)
    - Handles behaviours such as automatically scrolling (desktop) or swiping (mobile) to the next \`profiling-question\` when all the inputs it contains have been succesfully filled.
    `)

    doc-preview(vue=`
    profiling-question#developerQuestion(
      v-slot="{ goToNextQuestion }"
      question="What kind of developer are you ?"
      escort-message="We want to know more about you !"
      description="There is no wrong answer"
      :status="chosenOption ? 'success' : 'ongoing'"
    )
      profiling-radio(
        v-for="option in ['Fullstack', 'Front-end', 'Back-end']"
        :key="option"
        v-model="chosenOption"
        :radio-value="option"
        :error="false"
        :label="option"
        @input="goToNextQuestion()"
      )
    #nextQuestion
    `)
      div(class='w-4/5 mx-auto')
        profiling-question#developerQuestion(
          v-slot="{ goToNextQuestion }"
          question="What kind of developer are you ?"
          escort-message="We want to know more about you !"
          description="There is no wrong answer"
          :status="chosenOption ? 'success' : 'ongoing'"
        )
          profiling-radio(
            v-for="option in ['Fullstack', 'Front-end', 'Back-end']"
            :key="option"
            v-model="chosenOption"
            :radio-value="option"
            :error="false"
            :label="option"
            @input="goToNextQuestion()"
          )
        #nextQuestion

    doc-markdown(source=`
    Let's break down this component. It is made of:
    - A **scoped slot** where any amount of inputs can be injected. Corresponds to the right-hand side (on desktop)
    - The question, description guidance, the blue guide line and the escort speech bubble. Correspond to the left-hand side (on desktop). The content of that part can easily be customized with props: \`question\`, \`description\` and \`escort-message\`, as well as the positionning of these elements with some additional props: \`description-under-inputs-on-mobile\` and \`escort-under-inputs-on-mobile\`. The style of the blue guide bubble is itself determined by the \`status\` prop that accepts 3 possibles values (\`ongoing\`, \`success\` and \`error\`).
  Additionnally:
    - The display of a Profiling Question is **fully responsive**: thanks to a different arrangement of the elements described above between mobile and desktop, and with the help of the inner \`profiling-guide\` and \`sw-speech-ballon\` components, the presentation is adapted to the screen size. Note also that the body of the component is encapsulated out of the box in a simple \`<div>\` on desktop, while it is contained in the \`swiper-slide\` component on mobile, making it swipable.
    - **Functionnalities** are exposed by \`profiling-question\` and can be passed to inputs the scoped slot: \`goToNextQuestion()\` that automatically scrolls to the next question (desktop) or emit \`autoswipe\` (mobile) as soon as the status of the current one is \`success\`, and \`autoOpenNextSelect\` that automatically - in case of multiple \`profiling-select\` components in the scoped slot - opens the next one when the previous one is answered.
    `)

    img.rounded(src="@/images/profiling-question-scheme.png" class='max-w-screen-xl w-full mx-auto')

    doc-markdown(source=`
    ### Slots
  | Slot name        | Description                                                                                                                    |
  |------------------|--------------------------------------------------------------------------------------------------------------------------------|
  | *default*        | Recipient for all the inputs. This is a scoped slot so functions can be passed from the parent to the elements inside the slot.|
  | loading-element  | Can be used to display something while the default slot content is loading.                                                    |
    `)

    doc-markdown(source=`
    ### Properties
  | Props                                  | Description                                                                                                                | Type    | Default |
  |----------------------------------------|----------------------------------------------------------------------------------------------------------------------------|---------|---------|
  | \`question\`                           | The text of the main (bold) question                                                                                       | String  | -       |
  | \`description\`                        | The descriptive guidance text under the question (optional)                                                                | String  | -       |
  | \`description-under-inputs-on-mobile\` | Whether this descriptive should be positionned under the inputs on mobile.                                                 | Boolean | false   |
  | \`escort-message\`                     | The text inside the speech bubble (optional)                                                                               | String  | -       |
  | \`escort-under-inputs-on-mobile\`      | Whether the whole speech bubble should be positionned under the inputs on mobile.                                          | Boolean | false   |
  | \`status\`                             | The status of the current question (styles the blue left bubble). Accepts 3 possible values: 'ongoing', 'success', 'error' | String  | -       |
    `)

    doc-markdown(source=`
    ### Functions (exposed by v-slot)
  | Function                  | Description                                                                                                                                   |
  |---------------------------|-----------------------------------------------------------------------------------------------------------------------------------------------|
  | \`goToNextQuestion()\`    | Automatically scrolls to the next question (desktop) or emit \`autoswipe\` (mobile) as soon as the status of the current one is \`success\`.  |
  | \`autoOpenNextSelect()\`  | Automatically - in case of multiple \`profiling-select\` components in the scoped slot - opens the next one when the previous one is answered.|
    `)

    doc-markdown(source=`
    ### Events
  | Event name | Description                                                                                                                      | Parameters | Usage          |
  |------------|----------------------------------------------------------------------------------------------------------------------------------|:----------:|----------------|
  | autoswipe  | Triggered (on mobile only) when the Question's status become 'success', allowing to trigger an auto swiping action in response   | -          | \`@autoswipe\` |
    `)
</template>

<script>
export default {
  data () {
    return {
      chosenOption: null,
    };
  },
};
</script>
